import { useEffect, useState } from 'react';
import { ILocalization, IDesignerAppshellPayload, IPersonalizeOverlayPayload } from '../types';
import { PersonalizeOverlay } from './personalizeOverlay';
import { PersonalizeInfoOverlay } from './personalizeInfoOverlay';

interface IPersonalizeOverlyWrapperProps {
    localization: ILocalization;
    ssoId: string;
    isEsApp: boolean;
    isFullyRegistered: boolean;
}

export const PersonalizeOverlayWrapper = (props: IPersonalizeOverlyWrapperProps) => {
    const afterLoginParam = 'openLogoCreator';
    const appShellSignature = 'ESPP.LogoCreator.PersonalizeOverlayWrapper';

    const [poIsVisible, setPoIsVisible] = useState<boolean>(false);
    const [designerPayload, setDesignerPayload] = useState<IDesignerAppshellPayload>(null);
    const [logoServiceInfoUrl, setlogoServiceInfoUrl] = useState<string>(null);
    const [logoServiceRedirectUrl, setlogoServiceRedirectUrl] = useState<string>(null);
    const [isSmakeDesignerAvailable, setIsSmakeDesignerAvailable] = useState<boolean>(false);
    const [confirmRegistrationHintIsVisible, setConfirmRegistrationHintIsVisible] = useState<boolean>(false);

    useEffect(() => {
        // handle after login case
        if (window) {
            const searchParams = new URL(window.location.href).searchParams;

            if (searchParams.has(afterLoginParam)) {
                // get payload from param
                const payloadString = searchParams.get(afterLoginParam);
                const payload = decodePayload(payloadString);
                //open LogoCreator 
                openLogoCreatorDesigner(payload);
                // maybe LogoCreator is not loaded yet, make sure its opened when its ready
                window.shell.once('ESPP.LogoCreator.InitializationFinished',
                    () => {
                        openLogoCreatorDesigner(payload);
                    });

                //drop param;
                searchParams.delete(afterLoginParam);
                const paramsStr = searchParams.toString();
                const newUrl = window.location.protocol +
                    '//' +
                    window.location.host +
                    window.location.pathname +
                    (paramsStr ? '?' + paramsStr : '') +
                    window.location.hash;
                window.history.replaceState({}, 'unused', newUrl);
            }
        }
    }, []);

    useEffect(() => {
        if (window) {
            // open personalized overlay
            window.shell.subscribeTo('ESPP.LogoCreator.OpenPersonalizeOverlay',
                (payload: IPersonalizeOverlayPayload) => {
                    setlogoServiceInfoUrl(payload.logoServiceInfoUrl);
                    setlogoServiceRedirectUrl(payload.logoServiceRedirectUrl);
                    setDesignerPayload(payload.designerPayload);
                    setIsSmakeDesignerAvailable(payload.isSmakeDesignerAvailable);
                    setPoIsVisible(true);
                }, appShellSignature);

            // try to directly open LC
            window.shell.subscribeTo('ESPP.LogoCreator.TryToOpenDesignerWindow',
                (payload: IDesignerAppshellPayload) => {
                    setDesignerPayload(payload);
                    tryOpenDesigner(payload);
                }, appShellSignature);
        }
        return () => {
            window.shell.unsubscribeFrom('ESPP.LogoCreator.OpenPersonalizeOverlay', appShellSignature);
            window.shell.unsubscribeFrom('ESPP.LogoCreator.TryToOpenDesignerWindow', appShellSignature)
        };
    }, []);

    function closeOverlay(): void {
        setPoIsVisible(false);
    }

    function closeConfirmHint(): void {
        setConfirmRegistrationHintIsVisible(false);
    }

    function tryOpenDesigner(payload: IDesignerAppshellPayload) {
        if (props.ssoId && props.ssoId.length > 0) {
            //if user is logged in
            //close this PO modal
            setPoIsVisible(false);
            openLogoCreatorDesigner(payload);
        } else {
            // user not logged in yet
            //2. after AMS is done switching, open it 
            window.shell.once('ESCRM.AccountMenu.AccountMenuSwitchedToLoginRequestedMode', () => {
                //this opens the flyout
                window.shell.publishTo('ESMKT.HeadR.ToggleFlyout', 'LoginFlyout');
                //close this PO modal
                setPoIsVisible(false);
            });

            // 1. request AMS to switch its text to a message defined by us
            const currentPageUrl = new URL(window.location.href);
            const payload64 = encodePayload(payload);
            currentPageUrl.searchParams.set(afterLoginParam, payload64);

            // ESPP-11921 read current category from meta to make sure correct redirect behaivor
            const categoryValue: HTMLMetaElement | null = document.querySelector('meta[name="navkeypath"]');
            if (categoryValue) {
                currentPageUrl.searchParams.set('category', categoryValue.content);
            }

            const redirectUrl = currentPageUrl.pathname + currentPageUrl.search;

            if (props.isEsApp) {
                const urlWithParamEncoded = encodeURIComponent(redirectUrl);
                const param = '?loginRequired=true&redirectUrl=' + urlWithParamEncoded;

                const loginForEsAppWithRedirect = props.localization.esAppLoginPageUrl + param;

                window.location.href = loginForEsAppWithRedirect;
                return;
            }

            window.shell.publishTo('ESCRM.AccountMenu.ServiceRequestsLogin',
                {
                    redirectUrl: redirectUrl,
                    message: props.localization.personalizationLoginHint,
                });
        }
    }

    function openLogoCreatorDesigner(payload: IDesignerAppshellPayload) {
        if (!props.isFullyRegistered) {
            // open hint modal;
            setConfirmRegistrationHintIsVisible(true);
        } else {
            if (typeof window !== 'undefined' && window.shell)
                window.shell.publishTo('ESPP.LogoCreator.OpenDesignerWindow', payload);
        }
    }

    function handleDesignYourselfClick(
        payload: IDesignerAppshellPayload,
        smakeAvailable: boolean,
        redirectUrl: string
    ) {
        if (smakeAvailable) {
            tryOpenDesigner(payload);
        } else {
            window.open(redirectUrl, '_self');
        }
    }

    function encodePayload(payload: IDesignerAppshellPayload): string {
        const json = JSON.stringify(payload);
        const bytes = new TextEncoder().encode(json);
        const binary = String.fromCharCode(...bytes);
        return btoa(binary);
    }
    function decodePayload(encoded: string): IDesignerAppshellPayload {
        const binary = atob(encoded);
        const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
        const json = new TextDecoder().decode(bytes);
        return JSON.parse(json);
    }

    return <>
        <PersonalizeOverlay
            poIsVisible={poIsVisible}
            localization={props.localization}
            logoServiceInfoUrl={logoServiceInfoUrl}
            handleDesignYourselfClick={() => handleDesignYourselfClick(
                designerPayload,
                isSmakeDesignerAvailable,
                logoServiceRedirectUrl
            )}
            closeOverlay={closeOverlay}
        ></PersonalizeOverlay>
        <PersonalizeInfoOverlay
            localization={props.localization}
            confirmRegistrationHintIsVisible={confirmRegistrationHintIsVisible}
            closeOverlay={closeConfirmHint}
        ></PersonalizeInfoOverlay >
    </>
}
